/* Copyright 2013 - 2024 Waiterio LLC */

// ports below 1024 are considered privileged ports and require root access to bind

const products2id = {
  authrice: 1,
  waiterio: 2,
  tiramisu: 3,
  imagelato: 4,
  metricsmage: 5,
  multilocale: 6,
  polyblog: 7,
  supovia: 11,
  sharepa: 15,
  croncool: 16,
  churncatcher: 21,
  dododentist: 22,
  appskyline: 23,
  veterical: 24,
  wapiworld: 25,
  prosperanova: 26,
  dbmuse: 27,
  gammainvestments: 28,
  invoicevista: 29,
  hairdora: 30,
  doctofam: 31,
  hotelumo: 32,
  barberbrio: 33,
  bonitabook: 34,
  gymflexa: 35,
  opticalora: 36,
  mechaniczen: 37,
  pharmaphoria: 38,
  tutorializer: 39,
  pushello: 40,
  socketino: 41,
}

const services2id = {
  www: 1,
  app: 2,
  api: 3,
  whitelabel: 6,
  analytics: 7,
  tutorials: 8,
  fastlane: 9,
  workflows: 10,
}

const products2extraServices = {
  dododentist: {
    images: 4,
  },
  veterical: {
    images: 4,
  },
  hairdora: {
    images: 4,
  },
  doctofam: {
    images: 4,
  },
  gymflexa: {
    images: 4,
  },
  hotelumo: {
    images: 4,
  },
  metricsmage: {
    script: 4,
  },
  polyblog: {
    mcp: 4,
  },
  tutorializer: {
    mcp: 4,
  },
  supovia: {
    widget: 4,
    images: 5,
  },
  wapiworld: {
    mcp: 4,
  },
  prosperanova: {
    mcp: 4,
  },
  pushello: {
    mcp: 4,
  },
  waiterio: {
    images: 8,
    install: 4,
    geo: 5,
  },
}

const products = Object.keys(products2id)

const ports = {}

for (let p = 0; p < products.length; p += 1) {
  const product = products[p]
  ports[product] = {}

  let extraServices = products2extraServices[product] ?? {}
  let services2idWithExtraServices = { ...services2id, ...extraServices }
  const services = Object.keys(services2idWithExtraServices)

  for (let s = 0; s < services.length; s += 1) {
    const service = services[s]
    const productId = products2id[product]
    const serviceId = services2idWithExtraServices[service]

    // Single-digit serviceIds use the original `${productId}${serviceId}00`
    // server / `…${slice(-2)}` client formula. Two-digit serviceIds (added
    // when the original 1-9 slots ran out, starting with `workflows: 10`)
    // can't fit a trailing "00" without overflowing 65535 for 2-digit
    // productIds, so they get a 1-digit suffix instead: '0' for server,
    // '1' for client.
    let client
    let server

    if (serviceId < 10) {
      client = `${productId}${serviceId}`
      server = client + '00'
      client += `${client.slice(-2)}`
    } else {
      const prefix = `${productId}${serviceId}`
      server = prefix + '0'
      client = prefix + '1'
    }

    ports[product][service] = {
      client,
      server,
    }
  }
}

const clientServices = new Set(['app', 'script', 'tutorials', 'widget', 'www'])

export function getClientOrServer(service) {
  return clientServices.has(service) ? 'client' : 'server'
}

// ───────────────────────────── per-product Windows CDP debug ports
//
// The native C# / WinUI 3 / WebView2 shells under <product>/windows expose
// Chrome DevTools Protocol so Playwright + raw-WebSocket drivers can attach
// from the macOS host. Each product needs its own port so two Windows apps
// can run side-by-side without WebView2 caching a failed bind.
//
// `<product>/windows/AppConfig.cs:CdpPort` must equal this value. The C#
// const is the runtime source of truth; this JS expression is the design
// document — so the next product added gets a deterministic port without a
// fresh argument about numbering.
//
// Formula: 9200 + productId, so dododentist(22)=9222 (preserves the existing
// port — no migration), hotelumo(32)=9232.
//
// Doesn't fit the `${productId}${serviceId}…` formula above: every single-
// digit `serviceId` slot is already claimed (top-level + extras), and a
// two-digit slot blows past 65535.
for (let p = 0; p < products.length; p += 1) {
  const product = products[p]
  ports[product].windows = {
    cdpPort: String(9200 + products2id[product]),
  }
}

export default ports
